// =============================================================================
// CK — /admin/product-roadmap
// =============================================================================
// Roadmap dashboard with inline editor.
// Reads from Supabase: roadmap_quarters, roadmap_items, roadmap_tasks.
// Writes back on every edit (optimistic UI with rollback on error).
//
// Access gated by:
//   1. RLS on the three tables (server-side — the real gate)
//   2. isAdmin() check at component mount (client-side — UX)
//
// To add or remove an admin: edit src/adminAuth.js. RLS uses
// admin_users membership and is updated via SQL, not this file.
// =============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { supabase } from './supabaseClient';
import { isAdmin, getAdminProfile } from './adminAuth';


// ============================================================================
// Brand tokens
// ============================================================================
const C = {
  bg:           '#04060f',
  card:         '#0a1020',
  cardAlt:      '#0f1828',
  border:       '#1f2a40',
  gold:         '#f0c040',
  cyan:         '#00d4ff',
  green:        '#00e87a',
  purple:       '#b060ff',
  red:          '#ff4444',
  mutedBlue:    '#4a5a7a',
  textPrimary:  '#ffffff',
  textSecondary:'#a8b5cc',
  textMuted:    '#6b7a99',
};
const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_BODY    = "'Rajdhani', sans-serif";
const FONT_MONO    = "'Share Tech Mono', monospace";

const STATUS_COLOR = {
  shipped:     C.green,
  in_progress: C.cyan,
  upcoming:    C.gold,
  horizon:     C.mutedBlue,
};
const STATUS_LABEL = {
  shipped:     'SHIPPED',
  in_progress: 'IN PROGRESS',
  upcoming:    'UPCOMING',
  horizon:     'HORIZON',
};
const STATUS_OPTIONS = ['shipped', 'in_progress', 'upcoming', 'horizon'];

const ASSIGNEE_LABEL = {
  tomas: 'TOMÁS',
  esly:  'ESLY',
  both:  'BOTH',
};
const ASSIGNEE_COLOR = {
  tomas: C.gold,
  esly:  C.cyan,
  both:  C.purple,
};
const ASSIGNEE_OPTIONS = [null, 'tomas', 'esly', 'both'];


// ============================================================================
// Helpers
// ============================================================================
function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}


// ============================================================================
// Main component
// ============================================================================
export default function AdminProductRoadmap() {
  

  // ---- Auth gate ----------------------------------------------------------
  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!mounted) return;
      if (!isAdmin(user)) {
        window.location.href = '/';
      } else {
        setMe(getAdminProfile(user));
        setAuthReady(true);
      }
    });
    return () => { mounted = false; };
  }, []);

  // ---- State --------------------------------------------------------------
  const [quarters, setQuarters] = useState([]);
  const [items, setItems]       = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);

  const [openItemId, setOpenItemId]     = useState(null);
  const [filterQuarter, setFilterQuarter]   = useState('all');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');

  // ---- Data fetch ---------------------------------------------------------
  const refresh = useCallback(async () => {
    const [qRes, iRes, tRes] = await Promise.all([
      supabase.from('roadmap_quarters').select('*').order('sort_order'),
      supabase.from('roadmap_items').select('*').order('sort_order'),
      supabase.from('roadmap_tasks').select('*').order('sort_order'),
    ]);
    if (qRes.error) console.error('quarters fetch', qRes.error);
    if (iRes.error) console.error('items fetch',    iRes.error);
    if (tRes.error) console.error('tasks fetch',    tRes.error);
    setQuarters(qRes.data || []);
    setItems(iRes.data || []);
    setTasks(tRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authReady) refresh();
  }, [authReady, refresh]);

  // ---- Item mutations ------------------------------------------------------
  const updateItem = useCallback(async (id, patch) => {
    const prev = items.find(i => i.id === id);
    setItems(arr => arr.map(i => i.id === id ? { ...i, ...patch } : i));
    const { error } = await supabase
      .from('roadmap_items')
      .update({ ...patch, updated_by: prev?.updated_by ? prev.updated_by : null })
      .eq('id', id);
    if (error) { console.error('updateItem', error); refresh(); }
  }, [items, refresh]);

  const createItem = useCallback(async (quarterId) => {
    const title = window.prompt('Item title:');
    if (!title || !title.trim()) return;
    const baseId = slugify(title);
    if (!baseId) { window.alert('Title must contain letters or numbers.'); return; }
    let id = baseId;
    let n = 1;
    while (items.some(i => i.id === id)) { id = `${baseId}-${++n}`; }
    const maxSort = items
      .filter(i => i.quarter_id === quarterId)
      .reduce((m, i) => Math.max(m, i.sort_order || 0), 0);
    const newItem = {
      id,
      quarter_id: quarterId,
      title: title.trim(),
      status: 'upcoming',
      sort_order: maxSort + 1,
    };
    const { data, error } = await supabase
      .from('roadmap_items')
      .insert(newItem)
      .select()
      .single();
    if (error) { window.alert('Could not create item: ' + error.message); return; }
    setItems(arr => [...arr, data]);
    setOpenItemId(id);
  }, [items]);

  const deleteItem = useCallback(async (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    if (!window.confirm(`Delete "${item.title}" and all its tasks? This cannot be undone.`)) return;
    setItems(arr => arr.filter(i => i.id !== id));
    setTasks(arr => arr.filter(t => t.item_id !== id));
    setOpenItemId(null);
    const { error } = await supabase.from('roadmap_items').delete().eq('id', id);
    if (error) { console.error('deleteItem', error); refresh(); }
  }, [items, refresh]);

  const reorderItem = useCallback(async (id, direction) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    const peers = items
      .filter(i => i.quarter_id === item.quarter_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = peers.findIndex(p => p.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= peers.length) return;
    const a = peers[idx], b = peers[swapIdx];
    setItems(arr => arr.map(i => {
      if (i.id === a.id) return { ...i, sort_order: b.sort_order };
      if (i.id === b.id) return { ...i, sort_order: a.sort_order };
      return i;
    }));
    await Promise.all([
      supabase.from('roadmap_items').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('roadmap_items').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
  }, [items]);

  // ---- Task mutations ------------------------------------------------------
  const updateTask = useCallback(async (id, patch) => {
    setTasks(arr => arr.map(t => t.id === id ? { ...t, ...patch } : t));
    const { error } = await supabase.from('roadmap_tasks').update(patch).eq('id', id);
    if (error) { console.error('updateTask', error); refresh(); }
  }, [refresh]);

  const toggleTask = useCallback((task) => {
    updateTask(task.id, {
      completed: !task.completed,
      completed_by: !task.completed && me ? me.uuids[0] : null, // best-effort
    });
  }, [updateTask, me]);

  const createTask = useCallback(async (itemId) => {
    const label = window.prompt('Task label:');
    if (!label || !label.trim()) return;
    const maxSort = tasks
      .filter(t => t.item_id === itemId)
      .reduce((m, t) => Math.max(m, t.sort_order || 0), 0);
    const { data, error } = await supabase.from('roadmap_tasks').insert({
      item_id: itemId,
      label: label.trim(),
      sort_order: maxSort + 1,
    }).select().single();
    if (error) { window.alert('Could not create task: ' + error.message); return; }
    setTasks(arr => [...arr, data]);
  }, [tasks]);

  const deleteTask = useCallback(async (id) => {
    if (!window.confirm('Delete this task?')) return;
    setTasks(arr => arr.filter(t => t.id !== id));
    const { error } = await supabase.from('roadmap_tasks').delete().eq('id', id);
    if (error) { console.error('deleteTask', error); refresh(); }
  }, [refresh]);

  const reorderTask = useCallback(async (id, direction) => {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    const peers = tasks
      .filter(x => x.item_id === t.item_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = peers.findIndex(p => p.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= peers.length) return;
    const a = peers[idx], b = peers[swapIdx];
    setTasks(arr => arr.map(x => {
      if (x.id === a.id) return { ...x, sort_order: b.sort_order };
      if (x.id === b.id) return { ...x, sort_order: a.sort_order };
      return x;
    }));
    await Promise.all([
      supabase.from('roadmap_tasks').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('roadmap_tasks').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
  }, [tasks]);

  // ---- Computed -----------------------------------------------------------
  const itemsWithTasks = useMemo(() => {
    return items.map(item => {
      const itemTasks = tasks
        .filter(t => t.item_id === item.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const done = itemTasks.filter(t => t.completed).length;
      return {
        ...item,
        tasks: itemTasks,
        completedCount: done,
        totalCount: itemTasks.length,
      };
    });
  }, [items, tasks]);

  const filteredItems = useMemo(() => {
    return itemsWithTasks.filter(item => {
      if (filterQuarter !== 'all' && item.quarter_id !== filterQuarter) return false;
      if (filterStatus !== 'all'  && item.status     !== filterStatus)  return false;
      if (filterAssignee !== 'all') {
        const itemMatch =
          item.assignee === filterAssignee
          || (item.assignee === 'both' && (filterAssignee === 'tomas' || filterAssignee === 'esly'));
        const taskMatch = item.tasks.some(t =>
          t.assignee === filterAssignee
          || (t.assignee === 'both' && (filterAssignee === 'tomas' || filterAssignee === 'esly'))
        );
        if (!itemMatch && !taskMatch) return false;
      }
      return true;
    });
  }, [itemsWithTasks, filterQuarter, filterStatus, filterAssignee]);

  const byQuarter = useMemo(() => {
    const out = new Map();
    quarters.forEach(q => out.set(q.id, []));
    filteredItems.forEach(item => {
      if (out.has(item.quarter_id)) out.get(item.quarter_id).push(item);
    });
    out.forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return out;
  }, [quarters, filteredItems]);

  const totals = useMemo(() => {
    let done = 0, total = 0;
    itemsWithTasks.forEach(it => { done += it.completedCount; total += it.totalCount; });
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [itemsWithTasks]);

  const nextUp = useMemo(() => itemsWithTasks.find(it => it.status === 'in_progress'), [itemsWithTasks]);

  const openItem = openItemId ? itemsWithTasks.find(it => it.id === openItemId) : null;

  // ---- Render -------------------------------------------------------------
  if (!authReady || loading) {
    return <div style={{ background: C.bg, minHeight: '100vh' }} />;
  }

  return (
    <div style={{
      background: C.bg, color: C.textPrimary, fontFamily: FONT_BODY,
      minHeight: '100vh', padding: '32px 16px 64px',
    }}>
      <Styles />
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>

        {/* Header */}
        <div className="ck-crumb" onClick={() => window.location.href = '/admin'}>
          ← BACK TO ADMIN
        </div>
        <div className="ck-eyebrow">COLLECTION KEEPER · ADMIN · {me?.name?.toUpperCase() || 'ADMIN'}</div>
        <h1 className="ck-h1">ROADMAP</h1>
        <div className="ck-subtitle">// 2026 + 2027 HORIZON · TAP TO EDIT //</div>

        {/* Next Up hero */}
        {nextUp && (
          <NextUp item={nextUp} onOpen={() => setOpenItemId(nextUp.id)} />
        )}

        {/* Filter bar */}
        <FilterBar
          quarters={quarters}
          filterQuarter={filterQuarter} setFilterQuarter={setFilterQuarter}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          filterAssignee={filterAssignee} setFilterAssignee={setFilterAssignee}
          totals={totals}
        />

        {/* Quarters */}
        {quarters.map(q => {
          const qItems = byQuarter.get(q.id) || [];
          return (
            <div key={q.id}>
              <QuarterBand quarter={q} count={qItems.length} />
              {qItems.map(item => (
                <Item
                  key={item.id}
                  item={item}
                  onOpen={() => setOpenItemId(item.id)}
                />
              ))}
              <button
                className="ck-add-item-btn"
                onClick={() => createItem(q.id)}
                title="Add new item to this quarter"
              >
                + ADD ITEM TO {q.label.toUpperCase()}
              </button>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {openItem && (
        <ItemModal
          item={openItem}
          onClose={() => setOpenItemId(null)}
          updateItem={updateItem}
          deleteItem={deleteItem}
          reorderItem={reorderItem}
          updateTask={updateTask}
          toggleTask={toggleTask}
          createTask={createTask}
          deleteTask={deleteTask}
          reorderTask={reorderTask}
        />
      )}
    </div>
  );
}


// ============================================================================
// Sub-components
// ============================================================================

function NextUp({ item, onOpen }) {
  const pct = item.totalCount ? Math.round((item.completedCount / item.totalCount) * 100) : 0;
  return (
    <div className="ck-nextup" onClick={onOpen}>
      <div className="ck-nextup-label">// NEXT UP</div>
      <div className="ck-nextup-title">{item.title}</div>
      <div className="ck-nextup-meta">
        <span><strong>{item.completedCount}</strong> / {item.totalCount} tasks</span>
        {item.assignee && <span>· {ASSIGNEE_LABEL[item.assignee]}</span>}
        {item.deps && <span>· {item.deps}</span>}
      </div>
      <div className="ck-nextup-progress">
        <div className="ck-nextup-progress-fill" style={{ width: pct + '%' }} />
      </div>
    </div>
  );
}

function FilterBar({ quarters, filterQuarter, setFilterQuarter, filterStatus, setFilterStatus, filterAssignee, setFilterAssignee, totals }) {
  return (
    <div className="ck-filter-bar">
      <span className="ck-filter-label">QUARTER</span>
      <Chip active={filterQuarter === 'all'}    onClick={() => setFilterQuarter('all')}>ALL</Chip>
      {quarters.map(q => (
        <Chip key={q.id} active={filterQuarter === q.id} onClick={() => setFilterQuarter(q.id)}>
          {q.label}
        </Chip>
      ))}
      <span className="ck-chip-divider" />
      <span className="ck-filter-label">STATUS</span>
      <Chip active={filterStatus === 'all'}        onClick={() => setFilterStatus('all')}>ALL</Chip>
      {STATUS_OPTIONS.map(s => (
        <Chip key={s} active={filterStatus === s} onClick={() => setFilterStatus(s)} color={STATUS_COLOR[s]}>
          {STATUS_LABEL[s]}
        </Chip>
      ))}
      <span className="ck-chip-divider" />
      <span className="ck-filter-label">WHO</span>
      <Chip active={filterAssignee === 'all'}   onClick={() => setFilterAssignee('all')}>ALL</Chip>
      <Chip active={filterAssignee === 'tomas'} onClick={() => setFilterAssignee('tomas')} color={C.gold}>TOMÁS</Chip>
      <Chip active={filterAssignee === 'esly'}  onClick={() => setFilterAssignee('esly')}  color={C.cyan}>ESLY</Chip>
      <span className="ck-global-progress">
        <strong>{totals.pct}%</strong> · {totals.done}/{totals.total}
      </span>
    </div>
  );
}

function Chip({ active, color, onClick, children }) {
  return (
    <button
      className={'ck-chip' + (active ? ' active' : '')}
      onClick={onClick}
      style={active && color ? { borderColor: color, color } : undefined}
    >
      {children}
    </button>
  );
}

function QuarterBand({ quarter, count }) {
  const accentColor = C[quarter.accent] || C.cyan;
  return (
    <div className="ck-quarter-band">
      <div className="ck-quarter-label" style={{ color: accentColor }}>{quarter.label}</div>
      <div className="ck-quarter-rule" style={{ background: accentColor, opacity: 0.3 }} />
      <div className="ck-quarter-count" style={{ borderColor: accentColor, color: accentColor }}>
        {count} {count === 1 ? 'ITEM' : 'ITEMS'}
        {quarter.dates && ` · ${quarter.dates}`}
      </div>
    </div>
  );
}

function Item({ item, onOpen }) {
  const pct = item.totalCount ? Math.round((item.completedCount / item.totalCount) * 100) : 0;
  const statusColor = STATUS_COLOR[item.status] || C.textMuted;
  return (
    <div className="ck-item" onClick={onOpen}>
      <span className="ck-status-dot" style={{ background: statusColor }} />
      <div className="ck-item-header">
        {item.date_label && <span className="ck-item-date">{item.date_label}</span>}
        <span className="ck-item-title">{item.title}</span>
      </div>
      {item.summary && <div className="ck-item-summary">{item.summary}</div>}
      <div className="ck-item-meta">
        {item.workstream && <span className="ck-workstream-tag">WS {item.workstream}</span>}
        <span className={'ck-task-pill' + (item.completedCount === item.totalCount && item.totalCount > 0 ? ' complete' : '')}>
          {item.completedCount} / {item.totalCount}
        </span>
        {item.assignee && (
          <span className={'ck-assignee ' + item.assignee} style={{ color: ASSIGNEE_COLOR[item.assignee] }}>
            {ASSIGNEE_LABEL[item.assignee]}
          </span>
        )}
        {item.deps && <span style={{ color: C.gold }}>· {item.deps}</span>}
        <span className="ck-tap-hint">TAP →</span>
      </div>
      <div className="ck-mini-progress">
        <div className="ck-mini-progress-fill" style={{ width: pct + '%', background: statusColor }} />
      </div>
    </div>
  );
}

function ItemModal({ item, onClose, updateItem, deleteItem, reorderItem, updateTask, toggleTask, createTask, deleteTask, reorderTask }) {
  const pct = item.totalCount ? Math.round((item.completedCount / item.totalCount) * 100) : 0;
  return (
    <div className="ck-modal-backdrop" onClick={onClose}>
      <div className="ck-modal" onClick={e => e.stopPropagation()}>

        <div className="ck-modal-header">
          <div className="ck-modal-eyebrow">// EDITING ITEM</div>
          <EditableText
            value={item.title}
            onSave={v => updateItem(item.id, { title: v })}
            className="ck-modal-title"
            placeholder="Untitled item"
          />
          <div className="ck-modal-meta">
            <EditableSelect
              value={item.status}
              options={STATUS_OPTIONS}
              labelMap={STATUS_LABEL}
              colorMap={STATUS_COLOR}
              onSave={v => updateItem(item.id, { status: v })}
              prefix="STATUS:"
            />
            <EditableText
              value={item.date_label}
              onSave={v => updateItem(item.id, { date_label: v })}
              prefix="DATE:"
              placeholder="—"
            />
            <EditableText
              value={item.workstream}
              onSave={v => updateItem(item.id, { workstream: v })}
              prefix="WS:"
              placeholder="—"
            />
            <EditableSelect
              value={item.assignee}
              options={ASSIGNEE_OPTIONS}
              labelMap={{ ...ASSIGNEE_LABEL, null: 'UNASSIGNED' }}
              colorMap={ASSIGNEE_COLOR}
              onSave={v => updateItem(item.id, { assignee: v })}
              prefix="WHO:"
            />
          </div>
          <button className="ck-modal-close" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="ck-modal-body">

          <div className="ck-field-row">
            <span className="ck-field-label">SUMMARY</span>
            <EditableText
              value={item.summary}
              onSave={v => updateItem(item.id, { summary: v })}
              multiline
              placeholder="Tap to add a summary"
              className="ck-field-value"
            />
          </div>

          <div className="ck-field-row">
            <span className="ck-field-label">DEPENDENCIES</span>
            <EditableText
              value={item.deps}
              onSave={v => updateItem(item.id, { deps: v })}
              placeholder="Tap to add dependencies"
              className="ck-field-value"
            />
          </div>

          <div className="ck-modal-progress">
            <div className="ck-modal-progress-row">
              <span>TASKS</span>
              <span><span className="ck-count">{item.completedCount}</span> / {item.totalCount} · {pct}%</span>
            </div>
            <div className="ck-modal-progress-bar">
              <div className="ck-modal-progress-fill" style={{ width: pct + '%' }} />
            </div>
          </div>

          {item.tasks.map((task, idx) => (
            <TaskRow
              key={task.id}
              task={task}
              isFirst={idx === 0}
              isLast={idx === item.tasks.length - 1}
              toggleTask={toggleTask}
              updateTask={updateTask}
              deleteTask={deleteTask}
              reorderTask={reorderTask}
            />
          ))}

          <button className="ck-add-task-btn" onClick={() => createTask(item.id)}>
            + ADD TASK
          </button>
        </div>

        <div className="ck-modal-footer">
          <div className="ck-reorder-controls">
            <button className="ck-mini-btn" onClick={() => reorderItem(item.id, 'up')}   title="Move up">↑</button>
            <button className="ck-mini-btn" onClick={() => reorderItem(item.id, 'down')} title="Move down">↓</button>
          </div>
          <button className="ck-delete-btn" onClick={() => deleteItem(item.id)}>
            DELETE ITEM
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task, isFirst, isLast, toggleTask, updateTask, deleteTask, reorderTask }) {
  return (
    <div className={'ck-task' + (task.completed ? ' complete' : '')}>
      <div
        className="ck-task-check"
        onClick={() => toggleTask(task)}
        title={task.completed ? 'Mark incomplete' : 'Mark complete'}
      >
        {task.completed ? '✓' : ''}
      </div>
      <div className="ck-task-content">
        <EditableText
          value={task.label}
          onSave={v => updateTask(task.id, { label: v })}
          className="ck-task-label"
          placeholder="Task label"
        />
        <EditableText
          value={task.detail}
          onSave={v => updateTask(task.id, { detail: v })}
          className="ck-task-detail"
          placeholder="Tap to add detail"
        />
      </div>
      <div className="ck-task-side">
        <EditableSelect
          value={task.assignee}
          options={ASSIGNEE_OPTIONS}
          labelMap={{ ...ASSIGNEE_LABEL, null: '—' }}
          colorMap={ASSIGNEE_COLOR}
          onSave={v => updateTask(task.id, { assignee: v })}
          compact
        />
        <div className="ck-task-actions">
          {!isFirst && <button className="ck-mini-btn" onClick={() => reorderTask(task.id, 'up')}   title="Move up">↑</button>}
          {!isLast  && <button className="ck-mini-btn" onClick={() => reorderTask(task.id, 'down')} title="Move down">↓</button>}
          <button className="ck-mini-btn ck-mini-btn-danger" onClick={() => deleteTask(task.id)} title="Delete">✕</button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Inline editor primitives
// ============================================================================
function EditableText({ value, onSave, multiline, placeholder, className, prefix }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    const original = (value || '').trim();
    if (trimmed !== original) onSave(trimmed || null);
    setEditing(false);
  };
  const cancel = () => { setDraft(value || ''); setEditing(false); };

  if (!editing) {
    const display = value || '';
    return (
      <span
        className={(className || '') + ' ck-editable'}
        onClick={(e) => { e.stopPropagation(); setDraft(value || ''); setEditing(true); }}
        title="Tap to edit"
      >
        {prefix && <span className="ck-field-prefix">{prefix} </span>}
        {display || <em className="ck-placeholder">{placeholder || 'tap to edit'}</em>}
      </span>
    );
  }

  const Tag = multiline ? 'textarea' : 'input';
  return (
    <Tag
      ref={inputRef}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
        if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
      }}
      className="ck-editable-input"
      rows={multiline ? 3 : undefined}
      onClick={e => e.stopPropagation()}
    />
  );
}

function EditableSelect({ value, options, labelMap, colorMap, onSave, prefix, compact }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const currentLabel = (labelMap && labelMap[value === null ? 'null' : value]) || (value || '—');
  const currentColor = colorMap ? colorMap[value] : undefined;

  return (
    <span className={'ck-editable-select' + (compact ? ' compact' : '')} ref={ref}>
      <span
        className="ck-editable"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={currentColor ? { color: currentColor } : undefined}
      >
        {prefix && <span className="ck-field-prefix">{prefix} </span>}
        {currentLabel}
      </span>
      {open && (
        <div className="ck-select-dropdown" onClick={e => e.stopPropagation()}>
          {options.map(opt => {
            const lbl = (labelMap && labelMap[opt === null ? 'null' : opt]) || (opt || '—');
            const col = colorMap ? colorMap[opt] : undefined;
            const sel = opt === value;
            return (
              <div
                key={String(opt)}
                className={'ck-select-option' + (sel ? ' selected' : '')}
                onClick={() => { setOpen(false); if (opt !== value) onSave(opt); }}
                style={col ? { color: col } : undefined}
              >
                {sel ? '✓ ' : ''}{lbl}
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}


// ============================================================================
// Styles
// ============================================================================
function Styles() {
  return (
    <style>{`
      .ck-crumb { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 3px; color: ${C.textMuted}; cursor: pointer; margin-bottom: 14px; text-transform: uppercase; }
      .ck-crumb:hover { color: ${C.cyan}; }
      .ck-eyebrow { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 4px; color: ${C.cyan}; margin-bottom: 6px; text-transform: uppercase; }
      .ck-h1 { font-family: ${FONT_DISPLAY}; font-size: 44px; font-weight: 700; letter-spacing: 4px; margin: 0 0 4px; }
      .ck-subtitle { font-family: ${FONT_MONO}; font-size: 12px; letter-spacing: 3px; color: ${C.textMuted}; margin-bottom: 28px; }

      .ck-nextup {
        background: linear-gradient(135deg, rgba(0,212,255,0.06), rgba(0,232,122,0.04));
        border: 1px solid ${C.cyan}; border-radius: 6px;
        padding: 22px 26px; margin-bottom: 28px;
        position: relative; cursor: pointer;
        transition: box-shadow 0.15s;
      }
      .ck-nextup:hover { box-shadow: 0 0 24px rgba(0, 212, 255, 0.15); }
      .ck-nextup::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: ${C.cyan}; border-radius: 6px 0 0 6px; }
      .ck-nextup-label { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 4px; color: ${C.cyan}; text-transform: uppercase; margin-bottom: 8px; }
      .ck-nextup-title { font-family: ${FONT_DISPLAY}; font-size: 22px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; }
      .ck-nextup-meta { font-family: ${FONT_MONO}; font-size: 12px; letter-spacing: 2px; color: ${C.textSecondary}; text-transform: uppercase; display: flex; gap: 18px; flex-wrap: wrap; }
      .ck-nextup-meta strong { color: ${C.cyan}; }
      .ck-nextup-progress { margin-top: 14px; height: 4px; background: ${C.border}; border-radius: 2px; overflow: hidden; }
      .ck-nextup-progress-fill { height: 100%; background: linear-gradient(90deg, ${C.cyan}, ${C.green}); transition: width 0.3s; }

      .ck-filter-bar {
        display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        margin-bottom: 18px; padding: 14px 16px;
        background: ${C.card}; border: 1px solid ${C.border}; border-radius: 4px;
      }
      .ck-filter-label { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 3px; color: ${C.textMuted}; text-transform: uppercase; margin-right: 4px; }
      .ck-chip {
        font-family: ${FONT_MONO}; font-size: 10px; letter-spacing: 2px;
        padding: 4px 9px; border-radius: 999px; border: 1px solid ${C.border};
        background: transparent; color: ${C.textSecondary}; cursor: pointer;
        text-transform: uppercase; transition: all 0.12s;
      }
      .ck-chip:hover { border-color: ${C.textSecondary}; }
      .ck-chip.active { color: ${C.textPrimary}; border-color: ${C.textPrimary}; }
      .ck-chip-divider { height: 18px; width: 1px; background: ${C.border}; margin: 0 8px; }
      .ck-global-progress { margin-left: auto; font-family: ${FONT_MONO}; font-size: 12px; letter-spacing: 3px; color: ${C.textMuted}; }
      .ck-global-progress strong { color: ${C.gold}; font-size: 14px; }

      .ck-quarter-band { display: flex; align-items: center; gap: 16px; margin: 32px 0 16px; }
      .ck-quarter-label { font-family: ${FONT_DISPLAY}; font-size: 26px; font-weight: 700; letter-spacing: 4px; }
      .ck-quarter-rule { flex: 1; height: 1px; }
      .ck-quarter-count { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 3px; padding: 4px 10px; border-radius: 2px; text-transform: uppercase; border: 1px solid; }

      .ck-item {
        position: relative; padding: 16px 20px 16px 44px; margin-bottom: 12px;
        background: transparent; border: 1px solid ${C.border}; border-radius: 4px;
        cursor: pointer; transition: border-color 0.15s, background 0.15s;
      }
      .ck-item:hover { border-color: ${C.cyan}; background: rgba(0, 212, 255, 0.03); }
      .ck-status-dot { position: absolute; left: 20px; top: 22px; width: 10px; height: 10px; border-radius: 50%; }
      .ck-item-header { display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap; margin-bottom: 4px; }
      .ck-item-date { font-family: ${FONT_MONO}; font-size: 13px; letter-spacing: 2px; color: ${C.textSecondary}; white-space: nowrap; }
      .ck-item-title { font-family: ${FONT_BODY}; font-size: 17px; font-weight: 600; letter-spacing: 0.3px; }
      .ck-item-summary { font-family: ${FONT_MONO}; font-size: 12px; letter-spacing: 1px; color: ${C.textMuted}; }
      .ck-item-meta { display: flex; align-items: center; gap: 14px; margin-top: 10px; font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 2px; color: ${C.textMuted}; flex-wrap: wrap; }
      .ck-task-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px; border: 1px solid ${C.border}; border-radius: 999px; color: ${C.textSecondary}; }
      .ck-task-pill.complete { color: ${C.green}; border-color: rgba(0, 232, 122, 0.4); }
      .ck-workstream-tag { display: inline-flex; align-items: center; gap: 4px; color: ${C.textMuted}; font-weight: 700; }
      .ck-tap-hint { margin-left: auto; color: ${C.textMuted}; opacity: 0.6; }
      .ck-mini-progress { margin-top: 10px; height: 3px; background: ${C.border}; border-radius: 2px; overflow: hidden; }
      .ck-mini-progress-fill { height: 100%; transition: width 0.3s; }
      .ck-assignee { display: inline-flex; align-items: center; gap: 6px; font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; }

      .ck-add-item-btn {
        margin: 6px 0 24px;
        background: transparent;
        border: 1px dashed ${C.border};
        color: ${C.textMuted};
        font-family: ${FONT_MONO};
        font-size: 11px;
        letter-spacing: 3px;
        padding: 10px 16px;
        border-radius: 4px;
        cursor: pointer;
        text-transform: uppercase;
        width: 100%;
        transition: all 0.15s;
      }
      .ck-add-item-btn:hover { border-color: ${C.gold}; color: ${C.gold}; }

      /* Modal */
      .ck-modal-backdrop {
        position: fixed; inset: 0; background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(4px); display: flex; align-items: flex-start;
        justify-content: center; z-index: 100; padding: 32px 16px; overflow-y: auto;
      }
      .ck-modal {
        background: ${C.card}; border: 1px solid ${C.cyan}; border-radius: 6px;
        max-width: 720px; width: 100%; box-shadow: 0 0 80px rgba(0, 212, 255, 0.18);
      }
      .ck-modal-header { padding: 22px 26px 18px; border-bottom: 1px solid ${C.border}; position: relative; }
      .ck-modal-eyebrow { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 4px; color: ${C.cyan}; margin-bottom: 6px; text-transform: uppercase; }
      .ck-modal-title { font-family: ${FONT_DISPLAY}; font-size: 22px; font-weight: 700; letter-spacing: 1px; margin-bottom: 12px; display: block; }
      .ck-modal-meta { display: flex; gap: 22px; font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 2px; color: ${C.textMuted}; text-transform: uppercase; flex-wrap: wrap; }
      .ck-modal-meta strong { color: ${C.textSecondary}; }
      .ck-modal-close { position: absolute; top: 18px; right: 18px; width: 32px; height: 32px; background: transparent; border: 1px solid ${C.border}; border-radius: 4px; color: ${C.textSecondary}; cursor: pointer; font-family: ${FONT_MONO}; font-size: 18px; line-height: 1; display: flex; align-items: center; justify-content: center; }
      .ck-modal-close:hover { border-color: ${C.red}; color: ${C.red}; }

      .ck-modal-body { padding: 18px 26px 8px; }
      .ck-field-row { display: flex; flex-direction: column; gap: 4px; padding: 12px 0; border-bottom: 1px solid ${C.border}; }
      .ck-field-label { font-family: ${FONT_MONO}; font-size: 10px; letter-spacing: 3px; color: ${C.textMuted}; text-transform: uppercase; }
      .ck-field-value { font-family: ${FONT_BODY}; font-size: 14px; line-height: 1.4; color: ${C.textPrimary}; }
      .ck-field-prefix { color: ${C.textMuted}; font-weight: 400; margin-right: 4px; }

      .ck-modal-progress { padding: 16px 0; border-bottom: 1px solid ${C.border}; }
      .ck-modal-progress-row { display: flex; justify-content: space-between; font-family: ${FONT_MONO}; font-size: 12px; letter-spacing: 3px; margin-bottom: 8px; text-transform: uppercase; color: ${C.textMuted}; }
      .ck-modal-progress-row .ck-count { color: ${C.gold}; font-weight: 700; }
      .ck-modal-progress-bar { height: 6px; background: ${C.border}; border-radius: 3px; overflow: hidden; }
      .ck-modal-progress-fill { height: 100%; background: linear-gradient(90deg, ${C.cyan}, ${C.green}); transition: width 0.3s; }

      .ck-task { display: flex; align-items: flex-start; gap: 14px; padding: 13px 0; border-bottom: 1px solid ${C.border}; position: relative; }
      .ck-task:last-of-type { border-bottom: none; }
      .ck-task-check { flex-shrink: 0; width: 22px; height: 22px; border: 2px solid ${C.textMuted}; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin-top: 2px; color: ${C.bg}; font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.15s; }
      .ck-task-check:hover { border-color: ${C.green}; }
      .ck-task.complete .ck-task-check { background: ${C.green}; border-color: ${C.green}; }
      .ck-task-content { flex: 1; min-width: 0; padding-right: 8px; }
      .ck-task-label { font-family: ${FONT_BODY}; font-size: 16px; font-weight: 500; line-height: 1.35; display: block; }
      .ck-task.complete .ck-task-label { color: ${C.textMuted}; text-decoration: line-through; }
      .ck-task-detail { font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 1px; color: ${C.textMuted}; margin-top: 4px; display: block; }
      .ck-task-side { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
      .ck-task-actions { display: flex; gap: 4px; opacity: 0; transition: opacity 0.15s; }
      .ck-task:hover .ck-task-actions { opacity: 1; }

      .ck-mini-btn {
        background: transparent;
        border: 1px solid ${C.border};
        color: ${C.textMuted};
        font-family: ${FONT_MONO};
        font-size: 11px;
        padding: 3px 7px;
        border-radius: 3px;
        cursor: pointer;
      }
      .ck-mini-btn:hover { color: ${C.cyan}; border-color: ${C.cyan}; }
      .ck-mini-btn-danger:hover { color: ${C.red}; border-color: ${C.red}; }

      .ck-add-task-btn {
        font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 3px;
        padding: 8px 12px; border-radius: 999px; border: 1px solid ${C.gold};
        color: ${C.gold}; background: transparent; cursor: pointer;
        text-transform: uppercase; margin: 14px 0;
      }
      .ck-add-task-btn:hover { background: ${C.gold}; color: ${C.bg}; }

      .ck-modal-footer { padding: 14px 26px; border-top: 1px solid ${C.border}; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
      .ck-reorder-controls { display: flex; gap: 6px; }
      .ck-delete-btn {
        font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 3px;
        padding: 8px 14px; border-radius: 4px; border: 1px solid ${C.red};
        color: ${C.red}; background: transparent; cursor: pointer;
        text-transform: uppercase;
      }
      .ck-delete-btn:hover { background: ${C.red}; color: ${C.textPrimary}; }

      /* Editable primitives */
      .ck-editable {
        cursor: text;
        border-bottom: 1px dashed transparent;
        transition: border-color 0.15s, background 0.15s;
        padding: 1px 2px;
      }
      .ck-editable:hover {
        border-bottom-color: ${C.textMuted};
        background: rgba(255, 255, 255, 0.02);
      }
      .ck-placeholder { color: ${C.textMuted}; opacity: 0.6; font-style: italic; }
      .ck-editable-input {
        font: inherit; color: ${C.textPrimary};
        background: ${C.cardAlt};
        border: 1px solid ${C.cyan};
        border-radius: 3px;
        padding: 4px 6px;
        width: 100%;
        max-width: 100%;
        outline: none;
        resize: vertical;
        min-height: 24px;
      }

      .ck-editable-select { position: relative; display: inline-block; cursor: pointer; }
      .ck-select-dropdown {
        position: absolute; top: 100%; left: 0; margin-top: 4px;
        background: ${C.cardAlt}; border: 1px solid ${C.cyan};
        border-radius: 4px; padding: 4px;
        z-index: 200; min-width: 140px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
      }
      .ck-select-option {
        padding: 7px 10px;
        font-family: ${FONT_MONO}; font-size: 11px; letter-spacing: 2px;
        text-transform: uppercase; cursor: pointer;
        border-radius: 3px;
        color: ${C.textSecondary};
      }
      .ck-select-option:hover { background: rgba(0, 212, 255, 0.08); }
      .ck-select-option.selected { background: rgba(0, 212, 255, 0.12); color: ${C.cyan}; font-weight: 700; }

      /* Mobile */
      @media (max-width: 600px) {
        .ck-h1 { font-size: 32px; letter-spacing: 2px; }
        .ck-modal { border-radius: 0; }
        .ck-filter-bar { gap: 6px; }
        .ck-task-actions { opacity: 1; }
      }
    `}</style>
  );
}
